import { html, css, nothing, LitElement, PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import './mdtabs/tabs.js';
import type { MdTabs } from './mdtabs/tabs.js';
import '@digital-realty/ix-icon-button/ix-icon-button.js';

type ScrollDirection = 'left' | 'right';

export class IxTabs extends LitElement {
  static styles = css`
    :host {
      max-width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    :host(ix-tabs[inline]) {
      display: inline-flex;
    }
    :host(ix-tabs[inline]) ix-icon-button[disabled] {
      display: none;
    }
    md-tabs {
      flex-grow: 1;
      --md-primary-tab-container-color: transparent;
    }
    ix-icon-button {
      --md-icon-button-hover-state-layer-opacity: 0;
      --md-icon-button-pressed-state-layer-opacity: 0;
      --md-icon-button-icon-color: var(--md-sys-text-color-secondary);
      --md-icon-button-pressed-state-layer-color: var(
        --md-sys-text-color-secondary
      );
    }
    .scroll {
      cursor: pointer;
    }

    .gradient {
      position: relative;
    }

    .gradient.left {
      margin-right: -10px;
    }

    .gradient.right {
      margin-left: -10px;
    }

    .gradient::before {
      content: '';
      width: 20px;
      height: calc(100% + 2px);
      position: absolute;
      top: -2px;
      background: linear-gradient(to right, white, transparent);
      z-index: 10;
      pointer-events: none;
    }

    .gradient.left::before {
      left: 30px;
    }

    .gradient.right::before {
      right: 30px;
      transform: rotate(180deg);
    }

    .gradient.disabled::before {
      display: none;
    }
  `;

  @query('md-Tabs') mdTabs!: MdTabs;

  @query('ix-primary-tab[active]') activeTab!: Element;

  @property({ type: Boolean }) scrollButtons = false;

  @property({ type: Boolean }) hideScrollBar = false;

  @property({ type: Boolean, reflect: true }) inline = false;

  @property({ type: Boolean, attribute: 'left-align' }) alignLeft = false;

  @property({ type: Boolean }) private enableLeftScroll = true;

  @property({ type: Boolean }) private enableRightScroll = true;

  @property({ type: Boolean }) withoutBorderBottom = false;

  @state() private tabContainer: HTMLElement | null | undefined = null;

  get activeTabIndex() {
    return this.mdTabs.activeTabIndex;
  }

  set activeTabIndex(index: number) {
    this.mdTabs.activeTabIndex = index;
  }

  private calculateScrollButtonsEnabled() {
    if (!this.tabContainer) return;

    const { clientWidth, scrollWidth, scrollLeft } = this.tabContainer;
    this.enableLeftScroll = scrollLeft !== 0;
    this.enableRightScroll = scrollLeft !== scrollWidth - clientWidth;

    if (this.alignLeft) {
      const parent = this.parentElement?.clientWidth;
      if (parent && scrollWidth > parent) {
        this.inline = false;
      } else {
        this.inline = true;
      }
    }
  }

  private debounceEvent: ReturnType<typeof setTimeout> | undefined;

  private updateScrollButtonState = () => {
    clearTimeout(this.debounceEvent);
    this.debounceEvent = setTimeout(
      this.calculateScrollButtonsEnabled.bind(this),
      100
    );
  };

  async slotChange() {
    // Free event loop for next frame
    await 0;
    this.querySelector('ix-primary-tab[active]')?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
    });

    if (!this.tabContainer) return;

    if (this.scrollButtons && this.hideScrollBar) {
      this.tabContainer.classList.add('no-scrollbar');
    }

    if (this.scrollButtons) {
      this.calculateScrollButtonsEnabled();
      window.addEventListener('resize', this.updateScrollButtonState);
      this.tabContainer.addEventListener(
        'scroll',
        this.updateScrollButtonState
      );
    }

    await this.updateComplete;

    if (this.tabContainer && this.alignLeft) {
      const { clientWidth, scrollWidth } = this.tabContainer;
      this.inline = scrollWidth <= clientWidth;
    }
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    requestAnimationFrame(() => {
      this.tabContainer = this.mdTabs.shadowRoot?.querySelector('.tabs');
      this.slotChange();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.scrollButtons) {
      window.removeEventListener('resize', this.updateScrollButtonState);
      this.tabContainer?.removeEventListener(
        'scroll',
        this.updateScrollButtonState
      );
    }
  }

  private scrollTabs(direction: ScrollDirection = 'left', scrollAmount = 0.75) {
    if (this.tabContainer) {
      const { clientWidth, scrollLeft } = this.tabContainer;
      const offset =
        direction === 'left'
          ? clientWidth * -scrollAmount
          : clientWidth * scrollAmount;
      const scrollToPosition = scrollLeft + offset;
      this.tabContainer.scrollTo({
        top: 0,
        left: scrollToPosition,
        behavior: 'smooth',
      });
    }
  }

  render() {
    return html`
      ${this.scrollButtons
        ? html`<div
            class="gradient left ${!this.enableLeftScroll ? 'disabled' : ''}"
          >
            <ix-icon-button
              icon="chevron_left"
              @click=${() => this.scrollTabs('left')}
              ?disabled=${!this.enableLeftScroll}
            ></ix-icon-button>
          </div>`
        : nothing}
      <md-tabs .withoutBorderBottom="${this.withoutBorderBottom}">
        <slot @slotchange=${this.slotChange}></slot>
      </md-tabs>
      ${this.scrollButtons
        ? html`<div
            class="gradient right ${!this.enableRightScroll ? 'disabled' : ''}"
          >
            <ix-icon-button
              icon="chevron_right"
              @click=${() => this.scrollTabs('right')}
              ?disabled=${!this.enableRightScroll}
            ></ix-icon-button>
          </div>`
        : nothing}
    `;
  }
}
